import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * IT-F2-106 Phase 3d: dedicated per-scanner inline claims picker.
 *
 * Built from scratch WITHOUT Angular Forms / ngModel — every field
 * uses plain `[value]` / `(change)` handlers with immediate emit.
 * Phase 3b tried to reuse the shared claims-editor in a flat mode
 * and froze the browser tab on as-os's grouped-array schema
 * (8 subscanner groups × 3 tier options). Two defensive fixes
 * (_lastParsedKey memoization, _emitIfFlat re-entry guard) did not
 * break the freeze — the root cause is somewhere in the ngModel /
 * SelectControlValueAccessor init path we couldn't pin down
 * without a repro. This component sidesteps that machinery
 * entirely: no ngModel init emits, no reactive form controls, no
 * WeakMap-keyed option registration.
 *
 * Supports the same schema field types as the shared editor:
 *   - int (+ enum → <select>, otherwise <input type=number>)
 *   - string (+ enum → <select>, otherwise <input type=text>)
 *   - bool → <input type=checkbox>
 *   - stringbool → <select> with 'true'/'false' options (or enum)
 *   - array + enum → grouped <select> per subscanner (when labels
 *     match "Group - Mode") OR flat checkbox grid
 *   - array + options[] → flat checkbox grid (legacy allowlist)
 *
 * Schema resolution mirrors claims-editor: Scanner.claims_schema
 * wins; falls back to Customer.claims_schema when template.scanner
 * is unset or matches this scanner.
 *
 * State owned by parent: `entry` is a per-scanner claim object
 * `{scanner, ...fields}` — parent stores in Map<scannerId, entry>.
 * Every mutation emits `entryChange` with a fresh shallow-clone;
 * parent updates its Map on the emit.
 */

export interface SchemaField {
  key: string;
  label: string;
  type: 'int' | 'string' | 'bool' | 'stringbool' | 'array';
  default?: any;
  required?: boolean;
  enum?: { value: any; label: string }[];
  options?: string[];
  description?: string;
}

export interface ClaimsSchema {
  multiple_entries_per_scanner?: boolean;
  template: any;
  fields: SchemaField[];
}

@Component({
  standalone: true,
  selector: 'app-inline-scanner-claims',
  imports: [CommonModule],
  templateUrl: './inline-scanner-claims.component.html',
  // OnPush isolates this component from the parent users.component's
  // CD ticks — the outer form's implicit NgForm was likely running CD
  // on every mouse event which was touching our <select> nodes enough
  // to steal focus (Mike: "the focus is being pulled away by
  // something so if i try to tab through the options it doesn't work").
  // With OnPush we only re-render when inputs change reference OR the
  // internal (change) handlers mutate + emit. Native select can keep
  // focus.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineScannerClaimsComponent implements OnChanges {
  // Scanner doc (may carry claims_schema directly).
  @Input() scanner: any = null;
  // Owning customer doc (fallback claims_schema via .claims_schema).
  @Input() customer: any = null;
  // Current claim entry — { scanner, ...fields }. Empty object OK; the
  // template's `template` will seed defaults on first mount.
  @Input() entry: any = {};
  // Fires with a fresh shallow clone of `entry` after every mutation.
  @Output() entryChange = new EventEmitter<any>();

  schema: ClaimsSchema | null = null;
  fields: SchemaField[] = [];

  // Grouped-mode memoization by field identity — matches claims-editor.
  private readonly _GROUP_SEPARATOR = ' - ';
  private _isGroupedCache = new WeakMap<SchemaField, boolean>();
  private _groupsCache = new WeakMap<SchemaField, { group: string; options: { value: any; modeLabel: string }[] }[]>();

  ngOnChanges(_: SimpleChanges): void {
    // Resolve effective schema on any input change. Cheap — just picks
    // between two references; no allocation.
    const scannerSchema = this.scanner?.claims_schema;
    const custSchema = this.customer?.claims_schema;
    const custPin = custSchema?.template?.scanner || '';
    const scannerId = String(this.scanner?._id || this.scanner?.Id || '');
    const fallbackOk = !!custSchema && (!custPin || custPin === scannerId);
    this.schema = scannerSchema || (fallbackOk ? custSchema : null);
    this.fields = this.schema?.fields || [];
    // Seed template defaults on first mount — but only if entry is
    // empty AND we haven't already seeded (prevents overwriting a
    // parent-loaded value on re-render). Parent passes {} for a fresh
    // create; anything else is a hydrated entry.
    if (this.schema && this.entry && Object.keys(this.entry).length === 0) {
      const tmpl = JSON.parse(JSON.stringify(this.schema.template || {}));
      tmpl.scanner = scannerId;
      // Assign onto the same object reference so parent's Map value
      // sees the seeded fields without swapping references (which
      // would trigger a re-emit chain).
      Object.assign(this.entry, tmpl);
    }
  }

  /** Emit a fresh clone of entry so parent's === compare in ngOnChanges
   *  sees the change. Called from every field mutation. */
  private _emit() {
    this.entryChange.emit({ ...this.entry });
  }

  // ── Scalar handlers ─────────────────────────────────────────────

  onScalarChange(field: SchemaField, rawValue: any) {
    switch (field.type) {
      case 'int':
        this.entry[field.key] = Number.isFinite(Number(rawValue)) ? Number(rawValue) : (field.default ?? 0);
        break;
      case 'bool':
        this.entry[field.key] = !!rawValue;
        break;
      case 'stringbool':
        this.entry[field.key] = String(rawValue);
        break;
      case 'string':
      default:
        this.entry[field.key] = String(rawValue);
        break;
    }
    this._emit();
  }

  // ── Array/enum + grouped-array helpers ──────────────────────────

  private _asArray(v: any): any[] {
    if (Array.isArray(v)) return v;
    if (v !== undefined && v !== null && v !== '') return [v];
    return [];
  }

  isArrayValueSelected(field: SchemaField, value: any): boolean {
    return this._asArray(this.entry[field.key]).some(x => x === value);
  }

  toggleArrayValue(field: SchemaField, value: any) {
    let cur = this._asArray(this.entry[field.key]);
    if (cur.some(x => x === value)) cur = cur.filter(x => x !== value);
    else cur = [...cur, value];
    this.entry[field.key] = cur;
    this._emit();
  }

  isGroupedArrayField(field: SchemaField): boolean {
    const cached = this._isGroupedCache.get(field);
    if (cached !== undefined) return cached;
    const computed = this._computeIsGrouped(field);
    this._isGroupedCache.set(field, computed);
    return computed;
  }

  private _computeIsGrouped(field: SchemaField): boolean {
    if (field.type !== 'array' || !field.enum || field.enum.length < 2) return false;
    const groups = new Set<string>();
    for (const o of field.enum) {
      const label = String(o.label ?? '');
      const idx = label.indexOf(this._GROUP_SEPARATOR);
      if (idx <= 0 || idx >= label.length - this._GROUP_SEPARATOR.length) return false;
      groups.add(label.slice(0, idx).trim());
    }
    return groups.size >= 2;
  }

  arrayFieldGroups(field: SchemaField): { group: string; options: { value: any; modeLabel: string }[] }[] {
    const cached = this._groupsCache.get(field);
    if (cached) return cached;
    const out: { group: string; options: { value: any; modeLabel: string }[] }[] = [];
    const byGroup = new Map<string, { value: any; modeLabel: string }[]>();
    const order: string[] = [];
    for (const o of (field.enum || [])) {
      const label = String(o.label ?? '');
      const idx = label.indexOf(this._GROUP_SEPARATOR);
      const group = label.slice(0, idx).trim();
      const modeLabel = label.slice(idx + this._GROUP_SEPARATOR.length).trim();
      if (!byGroup.has(group)) { byGroup.set(group, []); order.push(group); }
      byGroup.get(group)!.push({ value: o.value, modeLabel });
    }
    for (const g of order) out.push({ group: g, options: byGroup.get(g)! });
    this._groupsCache.set(field, out);
    return out;
  }

  /** Which value from `groupOptions` is currently in entry[field.key],
   *  or '' if none. Returns a STRING (not the raw typed value) — the
   *  DOM's <select>.value property is always a string, so returning
   *  a number here caused Angular to see `.value !== binding` on every
   *  CD tick (`"3326" !== 3326` under `===`), redundantly re-setting
   *  select.value and closing any open dropdown mid-selection.
   *  Coerce back to the original enum type in onGroupSelectChange. */
  getGroupSelectedValue(field: SchemaField, groupOptions: { value: any; modeLabel: string }[]): string {
    const cur = this._asArray(this.entry[field.key]);
    const valueSet = new Set(groupOptions.map(o => o.value));
    for (const v of cur) if (valueSet.has(v)) return String(v);
    return '';
  }

  /** Same story for scalar selects — always return a string so
   *  <select [value]> stays stable across CD ticks. */
  scalarValueString(field: SchemaField): string {
    const v = this.scalarValue(field);
    return v === undefined || v === null ? '' : String(v);
  }

  onGroupSelectChange(field: SchemaField, groupOptions: { value: any; modeLabel: string }[], picked: string) {
    const valueSet = new Set(groupOptions.map(o => o.value));
    let cur = this._asArray(this.entry[field.key]).filter(x => !valueSet.has(x));
    if (picked !== '' && picked !== null && picked !== undefined) {
      // Values are stored typed — enum defines numeric values for
      // as-os. HTML <select> emits strings; coerce back to the
      // original enum type by looking it up.
      const opt = groupOptions.find(o => String(o.value) === String(picked));
      if (opt) cur = [...cur, opt.value];
    }
    this.entry[field.key] = cur;
    this._emit();
  }

  /** Read helper for scalar select/input value binding. */
  scalarValue(field: SchemaField): any {
    const v = this.entry[field.key];
    if (v === undefined || v === null) return field.default ?? '';
    return v;
  }
}
